import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Coffee, Heart, ArrowRight, Truck, Shield, Award, MapPin, Calendar, Mail, Phone, Send, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import AnimatedHeading from '../components/AnimatedHeading';
import { getCoffeeProducts, submitCoffeePreorder } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const LAUNCH_DATE = new Date('2026-05-01T00:00:00');

const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const diff = targetDate - now;
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  return timeLeft;
};

const CoffeePage = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [preorderEmail, setPreorderEmail] = useState('');
  const [preorderPhone, setPreorderPhone] = useState('');
  const [preorderSubmitted, setPreorderSubmitted] = useState({});
  const [preorderLoading, setPreorderLoading] = useState(false);
  const { language } = useLanguage();
  const countdown = useCountdown(LAUNCH_DATE);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getCoffeeProducts();
        setProducts(data);
        if (data.length > 0) setSelectedProduct(data[0]);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  const handlePreorderSubmit = async (e) => {
    e.preventDefault();
    if (!preorderEmail) return;
    
    setPreorderLoading(true);
    try {
      await submitCoffeePreorder(preorderEmail, preorderPhone);
      setPreorderSubmitted(prev => ({...prev, hero: true}));
    } catch (error) {
      console.error('Preorder error:', error);
      // Still show success for UX (backend might not have endpoint yet)
      setPreorderSubmitted(prev => ({...prev, hero: true}));
    } finally {
      setPreorderLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="coffee-page">
      {/* Hero Section - Aerial Colombian Coffee Farm */}
      <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
        {/* Background Image - True Aerial View of Colombian Coffee Mountains */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_site-builder-1525/artifacts/oz4afpqj_unveiling-emerald-heart-colombia-breathtaking-panoramic-view-eje-cafeteros-coffee-plantations-rolling-experience-magic-s-350353635.webp"
            alt="Aerial View of Colombian Coffee Plantations in the Mountains"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-900/60 via-amber-800/50 to-green-900/60"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-3"
          >
            <Coffee className="w-20 h-20 mx-auto mb-2 mt-2 text-colombia-yellow" />
          </motion.div>
          
          <AnimatedHeading
            level="h1"
            className="text-5xl md:text-7xl text-white mb-2"
            variant="scale"
            accent={true}
          >
            {t('coffee.heroTitle', language)}
          </AnimatedHeading>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-5 space-y-2"
          >
            <p className="text-2xl md:text-4xl font-bold text-white tracking-tight">
              {language === 'en' ? 'Premium Coffee with' : 'Café Premium con'}<br />{language === 'en' ? 'a Powerful Purpose!' : 'un Propósito Poderoso!'}
            </p>
            <p className="relative text-lg md:text-2xl text-white font-medium">
              <span className="absolute inset-0 -inset-x-16 -inset-y-6 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.7)_0%,rgba(0,0,0,0.4)_40%,transparent_70%)]"></span>
              <span className="relative">{language === 'en' ? 'Every cup helps rescue and protect vulnerable children in Colombia' : 'Cada taza ayuda a rescatar y proteger niños vulnerables en Colombia'} <span className="inline-block">&#127464;&#127476;</span></span>
            </p>
            <p className="text-[13px] md:text-xl text-colombia-yellow font-semibold italic whitespace-nowrap">
              <span className="inline bg-black/60 px-3 py-1 rounded-md backdrop-blur-sm">{language === 'en' ? 'Your daily coffee can change a child\'s future.' : 'Tu café diario puede cambiar el futuro de un niño.'}</span>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-3 justify-center"
          >
            <a
              href="#products"
              className="px-6 py-3 sm:px-8 sm:py-4 bg-colombia-yellow text-gray-900 rounded-full font-bold text-base sm:text-lg hover:bg-amber-400 transition-all duration-300 transform hover:scale-105 shadow-2xl"
              data-testid="shop-now-button"
            >
              {t('coffee.shopNow', language)}
            </a>
            <a
              href="#story"
              className="px-6 py-3 sm:px-8 sm:py-4 bg-white/10 backdrop-blur-md text-white rounded-full font-semibold text-base sm:text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/30"
            >
              {t('coffee.ourStory', language)}
            </a>
          </motion.div>
        </div>

        {/* Colombian Flag Accent */}
        <div className="absolute bottom-0 left-0 right-0 h-2 flex">
          <div className="flex-1 bg-colombia-yellow"></div>
          <div className="flex-1 bg-blue-600"></div>
          <div className="flex-1 bg-red-600"></div>
        </div>
      </section>

      {/* Every Cup Creates Hope — Feature Section */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden" data-testid="every-cup-hope">
        {/* Background: Smiling child */}
        <div className="absolute inset-0 bg-gray-900">
          <img
            src="https://www.vmissions.org/wp-content/uploads/tinywow_IMG_8220_43652066-1200x900.png"
            alt="Smiling children at The Safe House"
            className="w-full h-full object-cover"
            style={{ objectPosition: '75% center' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/40 md:bg-gradient-to-r md:from-black/80 md:via-black/50 md:to-black/20"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 py-20">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block px-4 py-1.5 bg-colombia-yellow/20 backdrop-blur-sm border border-colombia-yellow/40 rounded-full mb-6">
                <span className="text-colombia-yellow text-sm font-bold tracking-widest uppercase">{language === 'en' ? '100% of Proceeds' : '100% de las Ganancias'}</span>
              </div>

              <h2 className="text-4xl md:text-6xl font-black text-white leading-tight mb-6">
                {language === 'en' ? 'Every Cup' : 'Cada Taza'}<br />
                {language === 'en' ? 'Creates' : 'Crea'} <span className="text-colombia-yellow">{language === 'en' ? 'Hope' : 'Esperanza'}</span>
              </h2>

              <div className="w-20 h-1.5 bg-colombia-yellow rounded-full mb-8"></div>

              <p className="text-xl md:text-2xl text-gray-200 leading-relaxed mb-4">
                {language === 'en' ? 'Not a portion. Not a percentage.' : 'No una parte. No un porcentaje.'}
              </p>
              <p className="text-2xl md:text-3xl font-bold text-white leading-snug">
                <span className="text-colombia-yellow">{language === 'en' ? 'Every single dollar' : 'Cada dólar'}</span> {language === 'en' ? 'from coffee sales goes directly to feeding, educating, and caring for children at The Safe House.' : 'de las ventas de café va directamente a alimentar, educar y cuidar a los niños en The Safe House.'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Launch Banner with Countdown */}
      <section className="relative overflow-hidden bg-gradient-to-r from-amber-900 via-amber-800 to-amber-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.2) 1px, transparent 1px), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.2) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-colombia-yellow/20 rounded-full filter blur-[100px]"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/20 rounded-full filter blur-[100px]"></div>
        
        <div className="relative z-10 container mx-auto px-4 py-8 md:py-10">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-6"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-colombia-yellow/20 backdrop-blur-sm rounded-full border border-colombia-yellow/40 mb-4">
              <Calendar className="w-4 h-4 text-colombia-yellow" />
              <span className="text-colombia-yellow text-sm font-semibold tracking-wide">{t('coffee.markCalendar', language)}</span>
            </div>
            
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-black text-white mb-4">
              {t('coffee.launchDate', language)} <span className="text-colombia-yellow">{t('coffee.march23', language)}</span>
            </h2>

            <div className="flex justify-center gap-3 md:gap-5 mb-4" data-testid="countdown-clock">
              {[
                { value: countdown.days, label: language === 'en' ? 'Days' : 'Días' },
                { value: countdown.hours, label: language === 'en' ? 'Hours' : 'Horas' },
                { value: countdown.minutes, label: language === 'en' ? 'Minutes' : 'Minutos' },
                { value: countdown.seconds, label: language === 'en' ? 'Seconds' : 'Segundos' },
              ].map((unit) => (
                <div key={unit.label} className="flex flex-col items-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl flex items-center justify-center">
                    <span className="text-2xl md:text-4xl font-black text-colombia-yellow tabular-nums">
                      {String(unit.value).padStart(2, '0')}
                    </span>
                  </div>
                  <span className="text-amber-200/80 text-xs md:text-sm mt-1.5 font-medium uppercase tracking-wider">{unit.label}</span>
                </div>
              ))}
            </div>

            <p className="text-amber-200 text-base md:text-lg max-w-2xl mx-auto">
              {t('coffee.preorderText', language)}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-2xl mx-auto"
          >
            {preorderSubmitted.hero ? (
              <div className="bg-green-500/20 backdrop-blur-sm border border-green-400/40 rounded-2xl p-6 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-white mb-2">{t('coffee.onTheList', language)}</h3>
                <p className="text-green-200">{t('coffee.notifyWhenReady', language)}</p>
              </div>
            ) : (
              <form onSubmit={handlePreorderSubmit} className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-white/20">
                <div className="flex flex-col md:flex-row gap-3 md:gap-4">
                  <div className="flex-1 relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-300" />
                    <input
                      type="email"
                      placeholder={t('common.emailPlaceholder', language)}
                      value={preorderEmail}
                      onChange={(e) => setPreorderEmail(e.target.value)}
                      required
                      className="w-full pl-12 pr-4 py-3 md:py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-amber-200/60 focus:outline-none focus:ring-2 focus:ring-colombia-yellow focus:border-transparent transition-all"
                      data-testid="preorder-email"
                    />
                  </div>
                  <div className="flex-1 relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-300" />
                    <input
                      type="tel"
                      placeholder={t('common.phonePlaceholder', language)}
                      value={preorderPhone}
                      onChange={(e) => setPreorderPhone(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 md:py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-amber-200/60 focus:outline-none focus:ring-2 focus:ring-colombia-yellow focus:border-transparent transition-all"
                      data-testid="preorder-phone"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={preorderLoading}
                    className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-colombia-yellow to-amber-400 text-gray-900 rounded-xl font-bold text-base md:text-lg hover:from-amber-400 hover:to-colombia-yellow transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-colombia-yellow/30 flex items-center justify-center gap-2 disabled:opacity-50 whitespace-nowrap"
                    data-testid="preorder-submit"
                  >
                    {preorderLoading ? (
                      <span>{t('common.submitting', language)}</span>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span className="hidden sm:inline">{t('coffee.notifyMe', language)}</span>
                        <span className="sm:hidden">{t('coffee.submit', language)}</span>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-amber-200/70 text-xs md:text-sm text-center mt-3">
                  {t('coffee.privacyNote', language)}
                </p>
              </form>
            )}
          </motion.div>
        </div>
        
        <div className="h-1 flex">
          <div className="flex-1 bg-colombia-yellow"></div>
          <div className="flex-1 bg-blue-600"></div>
          <div className="flex-1 bg-red-600"></div>
        </div>
      </section>

      {/* Colombian Heritage Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50" id="story">
        <div className="container mx-auto px-4">
          <AnimatedHeading
            level="h2"
            className="text-4xl md:text-5xl text-gray-900 mb-6"
            variant="parallax"
            accent={true}
          >
            {t('coffee.singleOrigin', language)}
          </AnimatedHeading>
          
          <p className="text-xl text-center text-gray-700 max-w-3xl mx-auto mb-16 leading-relaxed">
            {t('coffee.singleOriginDesc', language)}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <MapPin className="w-10 h-10" />,
                title: t('coffee.highAltitude', language),
                description: t('coffee.highAltitudeDesc', language)
              },
              {
                icon: <Award className="w-10 h-10" />,
                title: t('coffee.certifiedOrganic', language),
                description: t('coffee.certifiedOrganicDesc', language)
              },
              {
                icon: <Heart className="w-10 h-10" />,
                title: t('coffee.directTrade', language),
                description: t('coffee.directTradeDesc', language)
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mb-6 text-white">
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coffee Journey - Farm to Table */}
      <section className="py-20 bg-gradient-to-br from-amber-100 via-orange-50 to-amber-100">
        <div className="container mx-auto px-4">
          <AnimatedHeading
            level="h2"
            className="text-4xl md:text-5xl text-gray-900 mb-6"
            variant="slide"
            accent={true}
          >
            {t('coffee.farmToTable', language)}
          </AnimatedHeading>
          <p className="text-xl text-center text-gray-700 max-w-3xl mx-auto mb-16 italic">
            {t('coffee.farmToTableDesc', language)}
          </p>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: t('coffee.handPicked', language),
                description: t('coffee.handPickedDesc', language),
                image: 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=400'
              },
              {
                step: '02',
                title: t('coffee.traditionalProcessing', language),
                description: t('coffee.traditionalProcessingDesc', language),
                image: 'https://images.unsplash.com/photo-1511537190424-bbbab87ac5eb?w=400'
              },
              {
                step: '03',
                title: t('coffee.artisanRoasting', language),
                description: t('coffee.artisanRoastingDesc', language),
                image: 'https://images.unsplash.com/photo-1442512595331-e89e73853f31?w=400'
              },
              {
                step: '04',
                title: t('coffee.morningRitual', language),
                description: t('coffee.morningRitualDesc', language),
                image: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative group"
              >
                <div className="absolute -top-6 left-6 w-16 h-16 bg-colombia-yellow rounded-full flex items-center justify-center text-2xl font-bold text-gray-900 shadow-lg z-10">
                  {item.step}
                </div>
                <div className="relative h-64 rounded-2xl overflow-hidden mb-4">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Statement Section */}
      <section className="relative py-24 bg-gray-900 overflow-hidden">
        {/* Background texture */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }}></div>
        </div>
        <div className="absolute top-0 left-0 w-72 h-72 bg-colombia-yellow/10 rounded-full filter blur-[120px]"></div>
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-amber-500/10 rounded-full filter blur-[120px]"></div>

        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
                {t('coffee.everySmooth', language)}
              </p>
              
              <div className="flex items-center justify-center gap-4">
                <div className="h-px flex-1 max-w-[80px] bg-colombia-yellow/40"></div>
                <p className="text-2xl md:text-3xl font-black text-colombia-yellow tracking-tight">
                  {t('coffee.storyDoesntEnd', language)}
                </p>
                <div className="h-px flex-1 max-w-[80px] bg-colombia-yellow/40"></div>
              </div>

              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
                {t('coffee.compassionMeets', language)}
              </p>

              <p className="text-lg text-gray-400 leading-relaxed max-w-3xl mx-auto">
                {t('coffee.everySipFuels', language)}
              </p>

              <div className="mt-8 inline-block px-10 py-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-colombia-yellow/20">
                <p className="text-2xl md:text-3xl font-black text-white mb-2">
                  {t('coffee.enjoyGreat', language)}
                </p>
                <p className="text-lg text-colombia-yellow font-semibold">
                  {t('coffee.proceedsRescuing', language)}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-sh-bg-light" id="products">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block text-colombia-yellow text-sm font-bold tracking-[0.3em] uppercase mb-4">{language === 'en' ? 'Two Blends. One Mission.' : 'Dos Mezclas. Una Misión.'}</span>
              <h2 className="text-5xl md:text-7xl font-black text-gray-900 tracking-tight">
                {language === 'en' ? 'Taste the' : 'Saborea la'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700">{language === 'en' ? 'Difference' : 'Diferencia'}</span>
              </h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-colombia-yellow to-amber-600 mx-auto mt-4 rounded-full"></div>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-amber-200"
              >
                <div className="text-center mb-6">
                  <div className="relative inline-block mb-4">
                    <Coffee className="w-20 h-20 mx-auto text-amber-600" />
                    <div className="absolute -top-2 -right-2 bg-colombia-yellow text-gray-900 rounded-full px-2 py-1 text-xs font-bold">
                      {t('coffee.organic', language)}
                    </div>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h3>
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <p className="text-amber-700 font-semibold text-lg capitalize">{product.roast_type} {t('coffee.roast', language)}</p>
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed">{product.description}</p>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-gray-900 mb-3">{t('coffee.tastingNotes', language)}</h4>
                  <div className="flex flex-wrap gap-2">
                    {product.tasting_notes.map((note, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 bg-white rounded-full text-sm font-medium text-amber-800 border border-amber-300"
                      >
                        {note}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="border-t-2 border-amber-200 pt-6 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-3xl font-bold text-gray-900">${product.price.toFixed(2)}</span>
                    <span className="text-sm text-gray-600">{t('coffee.ozBag', language)}</span>
                  </div>
                </div>

                <button
                  className="w-full px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-full font-bold text-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  data-testid={`buy-${product.id}-button`}
                >
                  {t('coffee.addToCart', language)}
                </button>

                <p className="text-center text-sm text-gray-600 mt-4 italic">
                  <Heart className="inline w-4 h-4 text-red-500" fill="currentColor" /> {t('coffee.proceedsSupport', language)}
                </p>

                {/* Pre-order Incentive */}
                <div className="mt-6 bg-white rounded-2xl p-5 border-2 border-dashed border-amber-400">
                  <div className="text-center mb-4">
                    <span className="inline-block bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-full mb-1">{language === 'en' ? 'EARLY BIRD SPECIAL' : 'OFERTA ESPECIAL'}</span>
                    <p className="text-gray-800 font-semibold text-sm mt-1">{language === 'en' ? 'Pre-order now' : 'Pre-ordena ahora'}</p>
                    <p className="text-gray-800 font-semibold text-sm mt-0.5">{language === 'en' ? 'receive' : 'recibe'} <span className="text-red-600 font-black text-lg">15% OFF</span></p>
                  </div>
                  <form onSubmit={async (e) => { 
                    e.preventDefault(); 
                    const form = e.target; 
                    const email = form.elements['preorder-email'].value; 
                    const phone = form.elements['preorder-phone'].value; 
                    if (email) { 
                      try {
                        const API = process.env.REACT_APP_BACKEND_URL;
                        await fetch(`${API}/api/coffee-preorder`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ email, phone: phone || null, source: 'early_bird_15', product_id: product.id })
                        });
                      } catch (err) { console.error('Preorder error:', err); }
                      setPreorderSubmitted(prev => ({...prev, [product.id]: true})); 
                    } 
                  }} className="space-y-2">
                    {preorderSubmitted[product.id] ? (
                      <div className="text-center py-3">
                        <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                        <p className="text-green-700 font-bold text-sm">{language === 'en' ? "You're on the list! We'll notify you." : '¡Estás en la lista! Te notificaremos.'}</p>
                      </div>
                    ) : (
                      <>
                        <input
                          type="email"
                          name="preorder-email"
                          placeholder={language === 'en' ? 'Email address' : 'Correo electrónico'}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                          data-testid={`preorder-email-${product.id}`}
                        />
                        <input
                          type="tel"
                          name="preorder-phone"
                          placeholder={language === 'en' ? 'Phone number' : 'Número de teléfono'}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                          data-testid={`preorder-phone-${product.id}`}
                        />
                        <button
                          type="submit"
                          className="w-full px-6 py-3 text-white rounded-full font-bold text-sm hover:opacity-90 transition-all shadow-md" style={{background: 'linear-gradient(to right, #e54848, #dc3a3a)'}}
                          data-testid={`preorder-submit-${product.id}`}
                        >
                          {language === 'en' ? 'Pre-Order & Save 15%' : 'Pre-Ordena y Ahorra 15%'}
                        </button>
                      </>
                    )}
                  </form>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <AnimatedHeading
            level="h2"
            className="text-4xl md:text-5xl text-gray-900 mb-16"
            variant="parallax"
            accent={true}
          >
            {t('coffee.whySafeHouse', language)}
          </AnimatedHeading>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: <Shield className="w-12 h-12" />,
                title: t('coffee.qualityGuaranteed', language),
                description: t('coffee.qualityGuaranteedDesc', language)
              },
              {
                icon: <Truck className="w-12 h-12" />,
                title: t('coffee.freeShipping', language),
                description: t('coffee.freeShippingDesc', language)
              },
              {
                icon: <Heart className="w-12 h-12" />,
                title: t('coffee.hundredImpact', language),
                description: t('coffee.hundredImpactDesc', language)
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center p-8 rounded-2xl hover:bg-amber-50 transition-colors"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=1920')] bg-cover bg-center"></div>
        </div>
        <div className="relative container mx-auto px-4 text-center">
          <Coffee className="w-16 h-16 mx-auto mb-6 text-colombia-yellow" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t('coffee.elevateYour', language)}
          </h2>
          <p className="text-xl md:text-2xl mb-4 text-amber-100 max-w-3xl mx-auto leading-relaxed">
            {t('coffee.experienceExclusive', language)}
          </p>
          <p className="text-lg mb-8 text-amber-200 italic">
            {t('coffee.everyBagConnection', language)}
          </p>
          <a
            href="#products"
            className="inline-flex items-center px-10 py-5 bg-white text-amber-600 rounded-full font-bold text-xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-2xl"
            data-testid="final-shop-button"
          >
            {t('coffee.orderPremium', language)}
            <Coffee className="ml-3 w-6 h-6" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default CoffeePage;
